import React, { useState, useEffect, useCallback } from 'react';
import { I18n } from 'aws-amplify';
import { Link } from 'react-router-dom';
import { createMuiTheme } from '@material-ui/core/styles';

import DefaultPatternSelector from './defaultPatternSelector.js';
import CalcTable from './calcTable.js';
import SubmitButton from './submitButton.js';

import {
  TERM_VER,
  API_STATUS,
  PPA_DEFAULTSETTING,
  PPA_PATTERNNAME_LENGTH
} from "../../../configures/val.js";
import checkTermsVer from '../../util/checkTermsVer.js';
import LapDialog from '../../util/laplaceDialog.js';
import authCheck, {
  getPpaService,
  postPpaDisplayPattern,
  putPpaDisplayPattern,
  deletePpaDisplayPattern,
  getPpaSid
} from '../../util/callApi.js';
import {
  isNumberAllowDecimal,
  isNumberAllowDecimalNotMinus,
  getNumDecimalPlace
} from '../../util/numberUtil.js';
import {
  isWhiteSpaceStr,
  isEmptyStr
} from '../../util/strUtil.js';

function PpaUserScreenSetting(props) {
  const [isHighlight, setHighlight] = useState(false); // ヘッダのハイライト状態を管理
  const [termError, setTermError] = useState(false); // 利用規約のポップアップ状態を管理
  const [authzApiStatus, setAuthzApiStatus] = useState(API_STATUS.LOADING); // 認可APIの状態
  const [serviceDataApiStatus, setServiceDataApiStatus] = useState(API_STATUS.LOADING); // サービスAPIの状態
  const [isKickServiceDataApi, setIsKickServiceDataApi] = useState(false); // ロード画面に遷移せずに Service API の呼び出しを管理するための状態管理値
  const [sid, setSid] = useState(''); // 事業者ID
  const [viewPatterns, setViewPatterns] = useState([]); // API から受け取った設定情報を管理
  const [samplePdfLocation, setSamplePdfLocation] = useState(''); // サンプルPDFのURL
  const [currentViewPatternNum, setCurrentViewPatternNum] = useState(0); // 現在表示状態にある設定パターンが何番目の設定か
  const [updateId, setUpdateId] = useState(-1); // 画面の表示対象にするパターンID
  const [deleteCheckList, setDeleteCheckList] = useState([]); // 削除のチェックボックスの状態を管理
  const [workingId, setWorkingId] = useState(0); // 編集中の設定ID
  const [patternName, setPatternName] = useState(''); // パターン名
  const [isEmptyPatternName, setIsEmptyPatternName] = useState(false); // パターン名が空欄
  const [isOverPatternName, setIsOverPatternName] = useState(false); // パターン名がPPA_PATTERNNAME_LENGTH を超過
  const [isAmountIncvoiced, setIsAmountIncvoiced] = useState(false); // 請求金額のチェック
  const [isContributionEnvironment, setIsContributionEnvironment] = useState(false); // 環境への貢献のチェック
  const [isMeasuredValue, setIsMeasuredValue] = useState(false); // 計測値のチェック
  const [isBattery, setIsBattery] = useState(false); // 蓄電池のチェック
  const [measuredItems, setMeasuredItems] = useState('allItems');// 計測値のラジオボタン
  const [basePrice, setBasePrice] = useState(0); // 基本料金
  const [reEneLevy, setReEneLevy] = useState(0); // 再エネ賦課金
  const [fuelAdjustmentCost, setFuelAdjustmentCost] = useState(0); // 燃料調整金
  const [errorBasePrice, setErrorBasePrice] = useState(false); // 基本料金のエラー表示
  const [errorReEneLevy, setErrorReEneLevy] = useState(false); // 再エネ賦課金の貢献のエラー表示
  const [errorFuelAdjustmentCost, setErrorFuelAdjustmentCost] = useState(false); // 燃料費調整のエラー表示
  const [calcTarget, setCalcTarget] = useState('consumption'); // 計算の設定対象
  const [errorHasCalcCondition, setErrorHasCalcCondition] = useState(false); // 計算式設定の表内に不正な値があるかどうか
  const [errorDisplay, setErrorDisplay] = useState(false); // 表示項目設定が1つ以上あるかどうか
  const [buyRows, setBuyRows] = useState(2); // 買電計算表の行数
  const [buySetting, setBuySetting] = useState([{ // 買電計算表のデータ
    over: 0,
    unitPrice: 0
  },{
    over: 0,
    unitPrice: 0
  }]);
  const [selfConsumptionRows, setSelfConsumptionRows] = useState(2);// 自家消費計算表の行数
  const [selfConsumptionSetting, setSelfConsumptionSetting] = useState([{// 自家消費計算表のデータ
    over: 0,
    unitPrice: 0
  },{
    over: 0,
    unitPrice: 0
  }]);
  const [consumptionRows, setConsumptionRows] = useState(2);// 消費電計算表の行数
  const [consumptionSetting, setConsumptionSetting] = useState([{// 自家消費電計算表のデータ
    over: 0,
    unitPrice: 0
  },{
    over: 0,
    unitPrice: 0
  }]);
  const energyGeneratedOnlyPattern = 'energyGeneratedOnly';

  // ヘッダハイライト
  useEffect(() => {
    if (!isHighlight) {
      props.updateHeader();
      setHighlight(true);
    }
  }, [props, isHighlight])
  // 認証ステータスと利用規約のバージョンを確認
  const confirmUserStatus = useCallback(async() => {
    const authRet = await authCheck();
    if (authRet.result === 'NG') {
      props.authStateChange('signIn');
      return true;
    }
    const isLowVer = checkTermsVer(authRet.data, 'custom:agreed_term', TERM_VER, 'ver');
    if (isLowVer) {
      setTermError(true);
      return true;
    }
    return false;
  }, [props]);
  // component mount 後に認証状態をチェックできればいいので、
  // 他の依存変数はなしにした
  useEffect(() => {
    confirmUserStatus();
  }, [confirmUserStatus]);
  // 事業者ID (sid) の取得
  useEffect(() => {
    async function innerCallApi() {
      let authzRet = {};
      if (authzApiStatus === API_STATUS.LOADING) {
        authzRet = await getPpaSid();
        if (authzRet.result === 'NG' || authzRet.data.supplier_ids.length === 0) {
          setAuthzApiStatus(API_STATUS.NO_BODY);
          return;
        } else if (authzRet.result === 'OK'){
          setAuthzApiStatus(API_STATUS.OK);
          const supplierIds = authzRet.data.supplier_ids;
          if (props.supplierId !== null) {
            if (supplierIds.includes(props.supplierId)) {
              setSid(props.supplierId);
            } else {
              props.signOut();
            }
          } else {
            setSid(supplierIds[0]);
          }
        }
      }
    }
    innerCallApi();
  }, [authzApiStatus, props]);
  // 現在の事業者IDに関連づいたユーザ画面設定の取得
  useEffect(() => {
    function updateCurrentViewPatternNum() {
      // id に対応する viewPattern が何番目かを返す
      // 何番目かを確認して setCurrentViewPattern に設定し対応する設定で画面を更新する
      for (let i = 0; i < viewPatterns.length; i++) {
        if (viewPatterns[i].id === updateId) {
          setCurrentViewPatternNum(i);
          return;
        }
      }
    }
    if (updateId > 0 && viewPatterns.length > 0) {
      updateCurrentViewPatternNum();
      return
    }
    // updateId が変わるときは、新規作成、コピー、更新時で、
    // APIに成功すると対応する id が返ってくるはずなのでこのケースは想定外だが
    // 画面としては 一番上のものを表示する
    setCurrentViewPatternNum(0);
  }, [viewPatterns, updateId]);
  // 現在の事業者IDに関連づいたユーザ画面設定の取得
  useEffect(() => {
    async function getLatestPatterns() {
      const serviceRet = await getPpaService(sid);
      if(serviceRet.result === 'OK') {
        setViewPatterns(serviceRet.data.displayPatterns);
        setSamplePdfLocation(serviceRet.data.samplePdfLocation);
        setServiceDataApiStatus(API_STATUS.OK);
      } else if(serviceRet.result === 'NG') {
        setServiceDataApiStatus(API_STATUS.NO_BODY);
      }
    }
    if (
      (serviceDataApiStatus === API_STATUS.LOADING && sid.length > 0) ||
      (!isKickServiceDataApi && sid.length > 0)
    ) {
      setIsKickServiceDataApi(true);
      getLatestPatterns();
    }
  }, [sid, serviceDataApiStatus, isKickServiceDataApi]);
  // 設定の画面反映
  useEffect(() => {
    const makeBuyDefault = () => {
      // いずれの計算表も初期は2行
      setBuyRows(2);
      setBuySetting([{
        over: 0,
        unitPrice: 0
      },{
        over: 0,
        unitPrice: 0
      }]);
    }
    const makeSelfConsumptionDefault = () => {
      setSelfConsumptionRows(2);
      setSelfConsumptionSetting([{
        over: 0,
        unitPrice: 0
      },{
        over: 0,
        unitPrice: 0
      }]);
    }
    const makeConsumptionDefault = () => {
      setConsumptionRows(2);
      setConsumptionSetting([{
        over: 0,
        unitPrice: 0
      },{
        over: 0,
        unitPrice: 0
      }]);
    }
    const makeCalcDefault = () => {
      setBasePrice(0);
      setReEneLevy(0);
      setFuelAdjustmentCost(0);
      setCalcTarget('consumption');
      makeBuyDefault();
      makeSelfConsumptionDefault();
      makeConsumptionDefault();
    }
    const updateSettingArea = (baseSettings) => {
      setWorkingId(baseSettings.id);
      setPatternName(baseSettings.patternName);
      // 表示項目設定
      let sceensLeng = baseSettings.screens.length;
      let isAmount = false;
      let isCEnvironment = false;
      let isMValue = false;
      for (let i = 0; i < sceensLeng; i++) {
        if (baseSettings.screens[i].asset.screenId === 'invoiceAmount') {
          isAmount = true;
          continue;
        }
        if (baseSettings.screens[i].asset.screenId === 'environmentatContribution') {
          isCEnvironment = true;
          continue;
        }
        if (baseSettings.screens[i].asset.screenId === 'skelton') {
          isMValue = true;
          setMeasuredItems(baseSettings.screens[i].measuredItems);
          if (baseSettings.screens[i].isBattery) {
            setIsBattery(baseSettings.screens[i].isBattery);
          } else {
            // undefined のとき false にする
            setIsBattery(false);
          }
          continue;
        }
      }
      setIsAmountIncvoiced(isAmount);
      setIsContributionEnvironment(isCEnvironment);
      setIsMeasuredValue(isMValue);
      // 計算式設定
      if (
        baseSettings.hasOwnProperty('invoiceCalculation') &&
        baseSettings.invoiceCalculation.length > 0
      ) {
        setBasePrice(baseSettings.invoiceCalculation[0].basePrice);
        setReEneLevy(baseSettings.invoiceCalculation[0].reEneLevy);
        setFuelAdjustmentCost(baseSettings.invoiceCalculation[0].fuelAdjustmentCost);
        setCalcTarget(baseSettings.invoiceCalculation[0].calcTarget);
        // 買電
        if (
          baseSettings.invoiceCalculation[0].pricingRange.hasOwnProperty('buy') &&
          baseSettings.invoiceCalculation[0].pricingRange.buy.length > 0
        ) {
          const {buy} = baseSettings.invoiceCalculation[0].pricingRange;
          setBuyRows(buy.length);
          setBuySetting(buy);
        } else {
          makeBuyDefault();
        }
        // 自家消費
        if (
          baseSettings.invoiceCalculation[0].pricingRange.hasOwnProperty('selfConsumption') &&
          baseSettings.invoiceCalculation[0].pricingRange.selfConsumption.length > 0
        ) {
          const {selfConsumption} = baseSettings.invoiceCalculation[0].pricingRange;
          setSelfConsumptionRows(selfConsumption.length);
          setSelfConsumptionSetting(selfConsumption);
        } else {
          makeSelfConsumptionDefault();
        }
        // 消費電
        if (
          baseSettings.invoiceCalculation[0].pricingRange.hasOwnProperty('consumption') &&
          baseSettings.invoiceCalculation[0].pricingRange.consumption.length > 0
        ) {
          const {consumption} = baseSettings.invoiceCalculation[0].pricingRange;
          setConsumptionRows(consumption.length);
          setConsumptionSetting(consumption);
        } else {
          makeConsumptionDefault();
        }
        if (isAmount) {
          // 請求画面ありの場合は計算式のバリデーション
          setErrorBasePrice(!validNum(baseSettings.invoiceCalculation[0].basePrice, 2));
          setErrorReEneLevy(!validNum(baseSettings.invoiceCalculation[0].reEneLevy, 2));
          setErrorFuelAdjustmentCost(!validNumAllowMinus(baseSettings.invoiceCalculation[0].fuelAdjustmentCost, 2));
        } else {
          // 請求画面がない場合は計算式のバリデーションは問わない
          setErrorBasePrice(false);
          setErrorReEneLevy(false);
          setErrorFuelAdjustmentCost(false);
          setErrorHasCalcCondition(false);
        }
      } else {
        makeCalcDefault();
      }
    }
    if (viewPatterns.length <= 0) {
      return;
    }
    if (viewPatterns[currentViewPatternNum]) {
      updateSettingArea(viewPatterns[currentViewPatternNum]);
    }
  }, [viewPatterns, currentViewPatternNum]);
  // 表示画面が存在しないようであればエラーで登録ボタンはクリックできないようにする
  const validateDisplaySetting = useCallback(() => {
    return !(isAmountIncvoiced || isContributionEnvironment || isMeasuredValue);
  }, [isAmountIncvoiced, isContributionEnvironment, isMeasuredValue]);
  useEffect(() => {
    setErrorDisplay(validateDisplaySetting());
  }, [isAmountIncvoiced, isContributionEnvironment, isMeasuredValue, validateDisplaySetting]);
  const validNum = (x, border) => {
    // 不正な数値でかつ小数点以下桁数が border 以上であれば
    return (isNumberAllowDecimalNotMinus(x) && (getNumDecimalPlace(x) <= border));
  }
  const validNumAllowMinus = (x, border) => {
    // 不正な数値でかつ小数点以下桁数が border 以上であれば
    return (isNumberAllowDecimal(x) && (getNumDecimalPlace(x) <= border));
  }
  const validateCalcSetting = useCallback((setting) => {
    for (let i = 0; i < setting.length; i++) {
      if (!validNum(setting[i]['over'], 0)) {
        return true;
      }
    }
    for (let i = 0; i < setting.length; i++) {
      if (!validNum(setting[i]['unitPrice'], 2)) {
        return true;
      }
    }
    return false;
  }, []);
  // 計算式設定に変更があれば validNum して登録ボタンが押せるかどうかを決める
  useEffect(() => {
    if (isAmountIncvoiced && calcTarget === 'consumption') {
      setErrorHasCalcCondition(validateCalcSetting(consumptionSetting));
      return;
    }
    if (isAmountIncvoiced && calcTarget === 'buyEnergy') {
      setErrorHasCalcCondition(validateCalcSetting(buySetting));
      return;
    }
    if (isAmountIncvoiced && calcTarget === 'selfConsumption') {
      setErrorHasCalcCondition(validateCalcSetting(selfConsumptionSetting));
      return;
    }
    if (isAmountIncvoiced && calcTarget === 'buyAndSelf') {
      setErrorHasCalcCondition(validateCalcSetting(buySetting));
      setErrorHasCalcCondition(validateCalcSetting(selfConsumptionSetting));
      return;
    }
  }, [buySetting, selfConsumptionSetting, consumptionSetting, validateCalcSetting, calcTarget, isAmountIncvoiced]);
  useEffect(() => {
    setIsOverPatternName(patternName.length > PPA_PATTERNNAME_LENGTH);
  }, [patternName]);

  const putDefaultPattern = async(selectedValue) => {
    if (await confirmUserStatus()) {
      return;
    }
    for (let i = 0; i < viewPatterns.length; i++) {
      // 設定一覧から選んだパターン名の設定を取り出し
      if (viewPatterns[i].id === selectedValue) {
        const updatedPattern = JSON.parse(JSON.stringify(viewPatterns[i]));
        updatedPattern.isDefault = true;
        const putRet = await putPpaDisplayPattern(viewPatterns[i].id, updatedPattern);
        if (putRet.result === 'NG') {
          alert(I18n.get('Failed to update.'));
        }
        setIsKickServiceDataApi(false);
        break;
      }
    }
  }
  const toggleDeleteCheck = (e, i) => {
    let tmp = JSON.parse(JSON.stringify(deleteCheckList));
    tmp[i] = !deleteCheckList[i];
    setDeleteCheckList(tmp);
  }
  // 削除のチェックリストを初期化
  const initDeleteCheckList = () => {
    let tmpDeleteList = [];
    for (let i = 0; i < viewPatterns.length; i++) {
      tmpDeleteList.push(false);
    }
    setDeleteCheckList(tmpDeleteList);
  }
  const deletePattern = async() => {
    if (await confirmUserStatus()) {
      return;
    }
    let deleteIdList = [];
    for (let i = 0; i < deleteCheckList.length; i++) {
      if (deleteCheckList[i]) {
        deleteIdList.push(deletePpaDisplayPattern(viewPatterns[i].id, sid));
      }
    }
    const allRet = await Promise.all(deleteIdList);
    for (let i = 0; i < allRet.length; i++) {
      if (allRet[i].result === 'NG') {
        alert(I18n.get('The deletion process failed.'));
        break;
      }
    }
    setIsKickServiceDataApi(false);
    // 削除時は 0 番目のパターンで画面を更新する
    setCurrentViewPatternNum(0);
    initDeleteCheckList();
  }
  const handleDeleteButtonDisabled = () => {
    for (let i = 0; i < deleteCheckList.length; i++) {
      if (deleteCheckList[i]) {
        return false;
      }
    }
    return true;
  }
  const makeDefaultPattern = () => {
    const tmp = JSON.parse(JSON.stringify(PPA_DEFAULTSETTING));
    tmp.supplierId = sid;
    const ary = [];
    const baseName = I18n.get('Pattern');
    for (let i = 0; i < viewPatterns.length; i++) {
      if(viewPatterns[i].patternName.indexOf(baseName) === 0) {
        const str = viewPatterns[i].patternName.slice(baseName.length);
        if (isNumberAllowDecimalNotMinus(Number(str))) {
          ary.push(Number(str));
        }
      }
    }
    // パターン-1 のみや、オリジナル名前のみのときは ary が空のままになるので
    if (!ary.length) {
      ary.push(0);
    }
    const nextId = Math.max(...ary) + 1;
    tmp.patternName = `${baseName}${nextId}`;
    tmp.createdAt = new Date().getTime();
    return tmp;
  }
  // デフォルトパターンを作ってそれを POST して新規作成
  const createPattern = async() => {
    if (await confirmUserStatus()) {
      return;
    }
    if (viewPatterns.length >= 10) {
      alert(I18n.get('You can only create a maximum of 10 patterns.'));
      return;
    }
    const defaultPattern = makeDefaultPattern();
    const postRet = await postPpaDisplayPattern(defaultPattern);
    if (postRet.result === 'NG') {
      alert(I18n.get('Fail to new registration'));
    }
    setIsKickServiceDataApi(false);
    setUpdateId(postRet.data.id);
  }
  const updatePatternName = (e) => {
    const {value} = e.target;
    setIsEmptyPatternName((isWhiteSpaceStr(value) || isEmptyStr(value)));
    setPatternName(value);
  }
  const handleDisplayItem = (e) => {
    const {name} = e.target;
    switch (name) {
      case "amountInvoiced":
        if (calcTarget === '') {
          setCalcTarget('consumption');
        }
        if (!isAmountIncvoiced === false) {
          // 請求画面を表示しない設定にするときは、計算式のバリデーションは問わない
          setErrorHasCalcCondition(false);
          setErrorBasePrice(false);
          setErrorReEneLevy(false);
          setErrorFuelAdjustmentCost(false);
        } else {
          // 請求画面を表示するときはバリデーションする
          setErrorBasePrice(!validNum(basePrice, 2));
          setErrorReEneLevy(!validNum(reEneLevy, 2));
          setErrorFuelAdjustmentCost(!validNumAllowMinus(fuelAdjustmentCost, 2));
        }
        setIsAmountIncvoiced(!isAmountIncvoiced);
        break;
      case "contributionEnvironment":
        setIsContributionEnvironment(!isContributionEnvironment);
        break;
      case "measuredValue":
        setIsMeasuredValue(!isMeasuredValue);
        break;
      default:
        break;
    }
  }
  const handleMeasuredItems = (e) => {
    const {value} = e.target;
    setMeasuredItems(value);
    if (value === energyGeneratedOnlyPattern) {
      setIsBattery(false);
      setIsAmountIncvoiced(false);
    }
  }
  // 入力の可不可を決める処理
  const validateAsEntryData = (value, isMinusOk) => {
    // 0のあとに数字は不可
    if (/^0+[0-9]/.test(value)) {
      return false;
    }
    // マイナスを入力させない
    if (!isMinusOk) {
      if (/-/.test(value)) {
        return false;
      }
    }
    let myVal;
    // 入力して良い文字 (列) でないのであれば、Number に変換。
    // 例えば全角文字とかであれば NaN に変わるのを狙う
    if (value !== '' || value !== '.' || value !== '-') {
      myVal = Number(value);
    }
    if (value === '' || value.match(/\./) || value.match(/-/)) {
      // 入力して良い文字 (列) であれば入力値として採用
      return true;
    } else {
      // 入力して良い文字 (列) でないのであれば妥当な値かどうかを検査する
      if (isNumberAllowDecimal(myVal)) {
        return true;
      }
    }
    return false;
  }
  const validateCommonPricing = (e) => {
    const {name, value} = e.target;
    switch (name) {
      case 'basePrice':
        if (!validateAsEntryData(value)) {
          return;
        }
        setErrorBasePrice(!validNum(value, 2));
        setBasePrice(value);
        break;
      case 'reEneLevy':
        if (!validateAsEntryData(value)) {
          return;
        }
        setErrorReEneLevy(!validNum(value, 2));
        setReEneLevy(value);
        break;
      case 'fuelAdjustmentCost':
        if (!validateAsEntryData(value, true)) {
          return;
        }
        setErrorFuelAdjustmentCost(!validNumAllowMinus(value, 2));
        setFuelAdjustmentCost(value);
        break;
      default:
        break;
    }
  }
  const updateCalcTarget = (e) => {
    const {value} = e.target;
    setCalcTarget(value);
  }
  const updateSubmitButtonDisabled = () => {
    return (
      errorDisplay ||
      errorHasCalcCondition ||
      errorBasePrice ||
      errorReEneLevy ||
      errorFuelAdjustmentCost ||
      isEmptyPatternName ||
      isOverPatternName
    );
  }
  // 登録時にもし inUse であればダイアログ表示をする必要があるので、
  // その判定に使う
  const getInUseByWorkingId = () => {
    for (let i = 0; i < viewPatterns.length; i++) {
      if (viewPatterns[i].id === workingId) {
        return viewPatterns[i].inUse;
      }
    }
  }
  const validateDuplicatePatternName = (nameStr) => {
    let targetName = '';
    if (nameStr) {
      targetName = nameStr;
    } else {
      targetName = patternName;
    }
    for (let i = 0; i < viewPatterns.length; i++) {
      if (viewPatterns[i].id === workingId) {
        // 今編集中の設定について判定対象に含めない
        continue;
      }
      if (viewPatterns[i].patternName === targetName) {
        alert(I18n.get('The name is the same as the registered pattern. Please change the pattern name.'));
        return true;
      }
    }
    return false;
  }
  const put = async() => {
    if (await confirmUserStatus()) {
      return;
    }
    if (validateDuplicatePatternName()) {
      return;
    }
    const param = makeSavePattern();
    const putRet = await putPpaDisplayPattern(workingId, param);
    if (putRet.result === 'NG') {
      alert(I18n.get('Failed to update.'));
    }
    setIsKickServiceDataApi(false);
    setUpdateId(putRet.data.id);
  }
  // 現在の state を集めて、saveData にして PUT
  const makeSavePattern = () => {
    return makePattern();
  }
  const makePattern = (newName, createdAt) => {
    let name = patternName;
    if (newName) {
      name = newName;
    }
    const res = {
      patternName: name,
      screens: [],
      supplierId: sid,
      invoiceCalculation: [{
        basePrice,
        reEneLevy,
        fuelAdjustmentCost,
        calcTarget,
        pricingRange: {
          buy: [],
          selfConsumption: [],
          consumption: []
        }
      }],
      coefficient: {
        smartPhone: 111,
        electricCar: 5.4
      }
    };
    if (isAmountIncvoiced) {
      res.screens.push({
        asset: {
          screenId: 'invoiceAmount'
        }
      });
    }
    if (isContributionEnvironment) {
      res.screens.push({
        asset: {
          screenId: 'environmentatContribution'
        }
      });
    }
    if (isMeasuredValue) {
      res.screens.push({
        asset: {
          screenId: 'skelton'
        },
        isBattery,
        measuredItems
      });
    }
    if (createdAt) {
      res.createdAt = createdAt;
    }
    res.invoiceCalculation[0].pricingRange.buy = buySetting;
    res.invoiceCalculation[0].pricingRange.selfConsumption = selfConsumptionSetting;
    res.invoiceCalculation[0].pricingRange.consumption = consumptionSetting;
    return res;
  }
  const copy = async() => {
    if (await confirmUserStatus()) {
      return;
    }
     if (viewPatterns.length >= 10) {
      alert(I18n.get('You can only create a maximum of 10 patterns.'));
      return;
    }
    const param = makeCopyPattern();
    if (!param) {
      return;
    }
    const postRet = await postPpaDisplayPattern(param);
    if (postRet.result === 'NG') {
      alert(I18n.get('Registration failed.'));
    }
    setIsKickServiceDataApi(false);
    setUpdateId(postRet.data.id);
  }
  const makeCopyPattern = () => {
    const addStr = I18n.get('Copy: ');
    const tmp = addStr + patternName;
    if (validateDuplicatePatternName(tmp)) {
      return false;
    }
    // newName の頭から PPA_PATTERNNAME_LENGTH に合わせて文字を切り取る
    const newName = tmp.substring(0, PPA_PATTERNNAME_LENGTH);
    const createdAt = new Date().getTime();
    return makePattern(newName, createdAt);
  }
  const closeDialog = () => {
    props.authStateChange('reagreeTermsDocs');
  }

  // render ===
  const makeViewPatternList = () => {
    return(
      <>
      {viewPatterns.map((viewPattern, i) => {
        const keyStr = `viewPattern${i}`;
        return(
          <div key={keyStr} className={i === currentViewPatternNum ? 'viewPattern select' : 'viewPattern'} onClick={() => setCurrentViewPatternNum(i)}>
            <div className="inputItem">
              <label className={viewPattern.inUse || viewPattern.isDefault ? 'checkBoxItemText checkDisabled' : 'checkBoxItemText'}>
                <input type="checkbox" checked={deleteCheckList[i]} onChange={(e) => toggleDeleteCheck(e, i)} disabled={viewPattern.inUse || viewPattern.isDefault} />
              </label>
            </div>
            <div className="nameItem">{viewPattern.patternName}</div>
          </div>
        );
      })}
      </>
    );
  }
  const showItemSetting = () => {
    return(
      <div className="displayItemSetting">
        <h5>{I18n.get('Display item setting')}</h5>
        <p>{I18n.get('You can select the screens and items to be displayed on the user\'s screen.')}
          <a href={samplePdfLocation}>{I18n.get('Click here for a screen sample')}</a>
        </p>
        <div className="displayItem">
          <div className="displayItemInner measurement">
            <label className='checkBoxItemText'><input type="checkbox" name="measuredValue" checked={isMeasuredValue} onChange={(e) => handleDisplayItem(e)} /></label>
            <span>A. {I18n.get('Measurement value (measurement value/graph): display the measurement value and graph.')}</span>
          </div>
          <div className={!isMeasuredValue ? 'disableBox measuredItems' : 'measuredItems'}>
            <h5>{I18n.get('Measurement value')}</h5>
            <div className={window.LAPLACEID_LOCALE === 'ja' ? "dispItem" : "dispItem ppaMeasuredItemParagraphEn"}>
              <div className="radioButtonItem">
                <label className='radioButtonItemText'>
                  <input
                    type="radio"
                    name="measuredItems"
                    value="allItems"
                    onChange={(e) => {handleMeasuredItems(e)}}
                    checked={measuredItems === 'allItems'}
                    disabled={!isMeasuredValue}
                  />
                </label>
                <span>1. {I18n.get('View all items')}</span>
              </div>
              <p>{I18n.get('Display items')} : {I18n.get('Energy generated')}, {I18n.get('Sold energy')}, {I18n.get('Purchased energy')}, {I18n.get('Energy consumption')}, {I18n.get('Self energy consumption')} ({I18n.get('daily data')})</p>
            </div>
            <div className={window.LAPLACEID_LOCALE === 'ja' ? "dispItem" : "dispItem ppaMeasuredItemParagraphEn"}>
              <div className="radioButtonItem">
                <label className='radioButtonItemText'>
                  <input
                    type="radio"
                    name="measuredItems"
                    value="noSell"
                    onChange={(e) => {handleMeasuredItems(e)}}
                    checked={measuredItems === 'noSell'}
                    disabled={!isMeasuredValue}
                  />
                </label>
                <span>2. {I18n.get('No sold energy')}</span>
              </div>
              <p>{I18n.get('Display items')} : {I18n.get('Energy generated')}, {I18n.get('Purchased energy')}, {I18n.get('Energy consumption')}, {I18n.get('Self energy consumption')} ({I18n.get('daily data')})</p>
            </div>
            <div className={window.LAPLACEID_LOCALE === 'ja' ? "dispItem" : "dispItem ppaMeasuredItemParagraphEn"}>
              <div className="radioButtonItem">
                <label className='radioButtonItemText'>
                  <input
                    type="radio"
                    name="measuredItems"
                    value="noSellConsumption"
                    onChange={(e) => {handleMeasuredItems(e)}}
                    checked={measuredItems === 'noSellConsumption'}
                    disabled={!isMeasuredValue}
                  />
                </label>
                <span>3. {I18n.get('No sold energy and energy consumption')}</span>
              </div>
              <p>{I18n.get('Display items')} : {I18n.get('Energy generated')}、{I18n.get('Purchased energy')}、{I18n.get('Self energy consumption')} ({I18n.get('daily data')})</p>
            </div>
            <div className={window.LAPLACEID_LOCALE === 'ja' ? "dispItem" : "dispItem ppaMeasuredItemParagraphEn"}>
              <div className="radioButtonItem">
                <label className='radioButtonItemText'>
                  <input
                    type="radio"
                    name="measuredItems"
                    value="noConsumption"
                    onChange={(e) => {handleMeasuredItems(e)}}
                    checked={measuredItems === 'noConsumption'}
                    disabled={!isMeasuredValue}
                  />
                </label>
                <span>4. {I18n.get('No energy consumption and self energy consumption')}</span>
              </div>
              <p>{I18n.get('Display items')} : {I18n.get('Energy generated')}、{I18n.get('Sold energy')}、{I18n.get('Purchased energy')} ({I18n.get('daily data')})</p>
            </div>
            <div className={window.LAPLACEID_LOCALE === 'ja' ? "dispItem" : "dispItem ppaMeasuredItemParagraphEn"}>
              <div className="radioButtonItem">
                <label className='radioButtonItemText'>
                  <input
                    type="radio"
                    name="measuredItems"
                    value={energyGeneratedOnlyPattern}
                    onChange={(e) => {handleMeasuredItems(e)}}
                    checked={measuredItems === energyGeneratedOnlyPattern}
                    disabled={!isMeasuredValue}
                  />
                </label>
                <span>5. {I18n.get('No sold energy and purchased energy and energy consumption and self energy consumption')}</span>
              </div>
              <p>{I18n.get('Display items')} : {I18n.get('Energy generated (daily data)')}</p>
            </div>
            <div className="dispItemBattery displayItemInner">
              <div className="checkBoxItem">
                <label>
                  <input
                    type="checkbox"
                    name="isBattery"
                    onChange={() => setIsBattery(!isBattery)}
                    checked={isBattery}
                    disabled={!isMeasuredValue || measuredItems === energyGeneratedOnlyPattern }
                />
                </label>
                <span>{I18n.get('Displays storage battery items in addition to the items selected above')}</span>
              </div>
              <p>{I18n.get('Additional display items')} : {I18n.get('Charged Power(daily data)')}, {I18n.get('Discharged Energy(daily data)')}, {I18n.get('SOC')}</p>
            </div>
            <div>
              <h5>{I18n.get('Graph')}</h5>
              <p>{I18n.get('Graph the selected item in the measurement value.')}</p>
            </div>
          </div>
        </div>
        <div className="displayItem">
          <div className="displayItemInner">
            <label className='checkBoxItemText'><input type="checkbox" name="contributionEnvironment" checked={isContributionEnvironment} onChange={(e) => handleDisplayItem(e)}/></label>
            <span>B. {I18n.get('Conversion of Energy generated: The measurement values are converted to the items around you and displayed.')}</span>
          </div>
        </div>
        <div className="displayItem">
          <div className="displayItemInner measurement">
            <label className='checkBoxItemText'><input type="checkbox" name="amountInvoiced" checked={isAmountIncvoiced} disabled={measuredItems === energyGeneratedOnlyPattern} onChange={(e) => handleDisplayItem(e)} /></label>
            <span>C. {I18n.get('Invoice amount (estimated amount/graph) : Displays estimated amount and graph of invoice amount')}</span>
          </div>
        </div>
      </div>
    );
  }
  const showCalcSettingArea = () => {
    return(
      <div className="commonPricing">
        <div className="renewableEnergyCost">
          <div className="titleItem">{I18n.get('Renewable Energy Levy')}</div>
          <div className="contentsItem">
            <input id="ppaRenewableEnergyCostText" type="text" name="reEneLevy" value={reEneLevy} onChange={(e) => validateCommonPricing(e)} disabled={!isAmountIncvoiced} />
            <span>{I18n.get('yen')} x {I18n.get('Purchased energy')}</span>
            {errorReEneLevy ? <p className='errorText'>{I18n.get('Please enter in the correct format (numbers, periods, or two decimal places)')}</p> : <p />}
          </div>
        </div>
        <div className="fuelCost">
          <div className="titleItem"><label htmlFor="ppafuelCostText">{I18n.get('Fuel cost adjustment')}</label></div>
          <div className="contentsItem">
            <input id="ppafuelCostText" type="text" name="fuelAdjustmentCost" value={fuelAdjustmentCost} onChange={(e) => validateCommonPricing(e)} disabled={!isAmountIncvoiced} />
            <span>{I18n.get('yen')} x {I18n.get('Purchased energy')}</span>
            {errorFuelAdjustmentCost ? <p className='errorText'>{I18n.get('Please enter in the correct format (numbers, periods, minus, or two decimal places)')}</p> : <p />}
          </div>
        </div>
      </div>
    );
  }
  const showSettingArea = () => {
    return(
      <div className="setting">
        <h4>{I18n.get('Setting')}</h4>
        <div className="settingInner">
          <div className="patternNameSetting">
            <div className="ppaViewPatternNameArea">
              <div className="ppaViewPatternNameItem">{I18n.get('Patterns name')}</div>
              <input id="ppaViewPatternName" type="text" value={patternName} onChange={(e) => updatePatternName(e)} />
            </div>
            <p className={isOverPatternName ? 'ppaPatternNameErrorText' : ''}>{I18n.get('Pattern name can set into 20 characters')}</p>
            {isEmptyPatternName ? <p className="errorPatternName">{I18n.get('Please enter a pattern name')}</p> : <p />}
          </div>
          {showItemSetting()}
          <div className="calcSetting">
            <h5>{I18n.get('Calculation setting')}</h5>
            <p>{I18n.get('You can set a formula for calculating the estimated amount to be billed. Calculation results are rounded down to the nearest yen.')}</p>
            <div className="commonPricing">
              <div className="baseCost">
                <div className="titleItem">{I18n.get('Basic fee / Minimum fee')}</div>
                <div className="contentsItem">
                  <input id="ppaBaseCostText" type="text" name="basePrice" value={basePrice} onChange={(e) => validateCommonPricing(e)} disabled={!isAmountIncvoiced} />
                  <span>{I18n.get('yen')}</span>
                  {errorBasePrice ? <p className='errorText'>{I18n.get('Please enter in the correct format (numbers, periods, or two decimal places)')}</p> : <p />}
                </div>
              </div>
            </div>
            <div className={!isAmountIncvoiced ? 'disableBox priceTarget' : 'priceTarget'}>
              <div className="priceTargetInner">
                <p>{I18n.get('Unit price setting')}</p>
                <div className="targetRadio">
                  <div className="radioButtonItem">
                    <label className='radioButtonItemText'>
                      <input type="radio" value="consumption" onChange={(e) => updateCalcTarget(e)} checked={calcTarget === 'consumption'} disabled={!isAmountIncvoiced} />
                    </label>
                    <span>{I18n.get('Energy consumption')} ({I18n.get('Purchased energy')} + {I18n.get('Self energy consumption')})</span>
                  </div>
                  <div className="radioButtonItem">
                    <label className='radioButtonItemText'>
                      <input type="radio" value="buyEnergy" onChange={(e) => updateCalcTarget(e)} checked={calcTarget === 'buyEnergy'} disabled={!isAmountIncvoiced} />
                    </label>
                    <span>{I18n.get('Purchased energy')}</span>
                  </div>
                  <div className="radioButtonItem">
                    <label className='radioButtonItemText'>
                      <input type="radio" value="selfConsumption" onChange={(e) => updateCalcTarget(e)} checked={calcTarget === 'selfConsumption'} disabled={!isAmountIncvoiced} />
                    </label>
                    <span>{I18n.get('Self energy consumption')}</span>
                  </div>
                  <div className="radioButtonItem">
                    <label className='radioButtonItemText'>
                      <input type="radio" value="buyAndSelf" onChange={(e) => updateCalcTarget(e)} checked={calcTarget === 'buyAndSelf'} disabled={!isAmountIncvoiced} />
                    </label>
                    <span>{I18n.get('Purchased energy')}・{I18n.get('Self energy consumption')}</span>
                  </div>
                </div>
              </div>
              {
                (
                  calcTarget === 'buyEnergy' ||
                  calcTarget === 'buyAndSelf'
                ) ?
                  <CalcTable
                    rows={buyRows}
                    setRows={setBuyRows}
                    setting={buySetting}
                    setSetting={setBuySetting}
                    disabled={!isAmountIncvoiced}
                    keyStr={'buy'}
                    validateAsEntryData={validateAsEntryData}
                    options={10}
                  />
                :
                  <div/>
              }
              {
                (
                  calcTarget === 'selfConsumption' ||
                  calcTarget === 'buyAndSelf'
                ) ?
                  <CalcTable
                    rows={selfConsumptionRows}
                    setRows={setSelfConsumptionRows}
                    setting={selfConsumptionSetting}
                    setSetting={setSelfConsumptionSetting}
                    disabled={!isAmountIncvoiced}
                    keyStr={'selfConsumption'}
                    validateAsEntryData={validateAsEntryData}
                    options={10}
                  />
                :
                  <div/>
              }
              {
                calcTarget === 'consumption' ?
                  <CalcTable
                    rows={consumptionRows}
                    setRows={setConsumptionRows}
                    setting={consumptionSetting}
                    setSetting={setConsumptionSetting}
                    disabled={!isAmountIncvoiced}
                    keyStr={'consumption'}
                    validateAsEntryData={validateAsEntryData}
                    options={10}
                  />
                :
                  <div />
              }
              { calcTarget === 'buyEnergy' || calcTarget === 'buyAndSelf' || calcTarget === 'consumption' ? showCalcSettingArea() : <div />}
            </div>
          </div>
        </div>
        <div className="settingButtons">
          <button className="editButton" onClick={copy} disabled={updateSubmitButtonDisabled()}>{I18n.get('Copy')}</button>
          <SubmitButton
            theme={theme}
            put={put}
            getInUseByWorkingId={getInUseByWorkingId}
            updateSubmitButtonDisabled={updateSubmitButtonDisabled}
          />
        </div>
        {errorDisplay ? <p className='errorText'>{I18n.get('Please select at least one screen.')}</p> : <p />}
      </div>
     );
  }
  const handleUserViewSettingContainer = () => {
    if (authzApiStatus === API_STATUS.LOADING) {
      return(<p>{I18n.get('Obtaining a business ID')}</p>);
    }
    if (authzApiStatus === API_STATUS.NO_BODY) {
      return(<p>{I18n.get('Failure to obtain a business ID')}</p>);
    }
    if (serviceDataApiStatus === API_STATUS.LOADING) {
      return(<p>{I18n.get('Obtaining PPA setting data')}</p>);
    }
    if (serviceDataApiStatus === API_STATUS.NO_BODY) {
      return(<p>{I18n.get('Failure to obtain PPA setting data')}</p>);
    }
    return (
      <>
        <DefaultPatternSelector
          viewPatterns={viewPatterns}
          putDefaultPattern={putDefaultPattern}
        />
        <div id="ppaUserViewSettingContainer">
          <div className="viewPatternManager">
            <h4>{I18n.get('Patterns name')}</h4>
            {makeViewPatternList()}
            <div className="viewPatternButtons">
              <button className="deleteButton" onClick={deletePattern} disabled={handleDeleteButtonDisabled()}>{I18n.get('Delete')}</button>
              <button className="editButton blue" onClick={createPattern}>{I18n.get('Create')}</button>
            </div>
          </div>
          {showSettingArea()}
        </div>
      </>
    );
  }
  return (
    <div className="mypageContainer">
      <div className='mypageBreadcrumbs'>
        <Link to='/servicelist'>{I18n.get('Service list')}</Link>&nbsp;&nbsp;＞&nbsp;&nbsp;{I18n.get('User screen setting')}
      </div>
      {handleUserViewSettingContainer()}
      <LapDialog
        title={''}
        contentElem={
          <div>
          <p>{I18n.get('Terms of Service is updated.')}</p>
          <p>{I18n.get('Please reconfirm it.')}</p>
          </div>
        }
        firstButtonText={'OK'}
        firstButtonFunc={() => closeDialog()}
        open={termError}
        toClose={() => {return}}
      />
    </div>
  );
}

const theme = createMuiTheme({
  palette: {
    primary: {
      light: '#ffffff',
      main: '#d6d6d6',
      dark: '#a5a5a5',
      contrastText: '#666666',
    },
    secondary: {
      light: '#ffebdb',
      main: '#ffb8a9',
      dark: '#ff9f8b',
      contrastText: '#f62b00',
    },
  },
});
export default PpaUserScreenSetting;
